/**
 * @file Browser timezone capture for sign-in surfaces.
 * @module @labcraft/core/frontend/timezone
 *
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` is the runtime's
 * IANA zone (e.g. `America/New_York`) — read silently, no permission
 * prompt. The sign-in surfaces (www's OAuth redirect + email mutation,
 * lab's `/authorize` card + client OAuth methods) capture it and
 * forward it to the account daemon, which writes it to
 * `learners.timezone` on first sign-up so the operator can compose
 * batch-activation cohorts across zones.
 *
 * Lives in `@labcraft/core/frontend` (not `@labcraft/ui`) because lab's
 * transport-only `src/client` — which builds the OAuth redirect URL —
 * cannot import React, and the `@labcraft/ui` chrome barrel is React.
 * This module is React-free, so every sign-in caller shares one copy.
 *
 * Best-effort: a runtime that can't resolve a zone yields `undefined`,
 * and the account daemon drops any value it can't validate as IANA — a
 * missing or bogus zone never blocks sign-in.
 */

/**
 * The runtime's IANA timezone, or `undefined` when it can't be
 * resolved.
 *
 * @returns The resolved IANA zone string (e.g. `America/New_York`), or
 *   `undefined`.
 */
export function browserTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}
